import { memo, useCallback, useEffect, useRef } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import to from 'await-to-js';
import { Undo2 } from 'lucide-react';
import Path from 'path-to-regexp';
import { useHistory, useLocation, useParams } from 'react-router';

import { Button } from '@/shadcn/button';

import {
  type GroupFilter,
  GroupsOrderBy,
  type InputMaybe,
  type WorkflowGroupsQueryQuery
} from '@/apollo/graphql';
import { Navbar } from '@/components/layouts';
import type { NavbarConfig } from '@/components/layouts/header';
import { notify } from '@/components/toast';
import {
  ROUTE_GROUP_SETTINGS,
  ROUTE_USER_DASHBOARD,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import {
  ADD_GROUP_MUTATION,
  WORKFLOW_GROUPS_QUERY
} from '@/gql/queries/workflowGroups/workflowGroups.query.gql';
import { useErrorContext } from '@/hooks/useErrorContext';
import { TestId } from '@/types/testId';
import { useInput, useToggle } from '@/utils/hooks';
import { AddGroupModal } from './addGroupModal/addGroupModal';
import { GroupsList } from './groupsList/groupsList';
import { SubHeader } from './subHeader/subHeader';

// some constants specific to workflow groups
const GROUPS_PAGINATION_SIZE = 20 as const;
const GROUPS_SEARCH_DEBOUNCE = 250 as const; // delay before running a new search query when text is typed

export const WorkflowGroups = () => {
  const { handleError } = useErrorContext();
  const history = useHistory();
  const location = useLocation<{ from: string }>();
  const params = useParams<{ id: string }>();

  const [addGroupMutation] = useMutation(ADD_GROUP_MUTATION);

  const { data, loading, error, fetchMore, refetch } = useQuery(
    WORKFLOW_GROUPS_QUERY,
    {
      variables: {
        workflowRowId: params.id,
        orderBy: GroupsOrderBy.NameDesc,
        first: GROUPS_PAGINATION_SIZE,
        filter: undefined
      },
      fetchPolicy: 'cache-and-network'
    }
  );

  // modal state
  const [showAddGroupModal, switchShowAddGroupModal] = useToggle(false);

  const workflow = data?.workflowByRowId;

  // set doc title at mount
  useEffect(() => {
    const { name } = workflow || {};
    if (name) document.title = `${name} - Groups - Trace`;
  }, [workflow]);

  // Raise 404 page when workflow not found.
  const workflowError = error || !(loading || workflow);
  useEffect(() => {
    // This check avoids rerendering the component after the erroBoundary has been triggered
    if (workflowError) {
      handleError('workflow', params.id, ROUTE_USER_DASHBOARD, error);
    }
  }, [workflowError, params, handleError]);

  // refetch data when the search input is updated
  const refetchGroupsWithSearch = useCallback(
    (searchValue: string) => {
      const { rowId } = workflow || {};
      refetch({
        workflowRowId: rowId,
        orderBy: GroupsOrderBy.NameDesc,
        first: GROUPS_PAGINATION_SIZE,
        filter: getFilterSetupFromSearchStr(searchValue)
      });
    },
    [workflow]
  );

  // search groups state linked to input field and refetch debounced callback
  const [searchGroups, updateSearch] = useInput(
    '',
    refetchGroupsWithSearch,
    GROUPS_SEARCH_DEBOUNCE
  );

  // go back to source url
  const onGoBackClick = useCallback(() => {
    if (location.state && location.state.from) {
      history.push(location.state.from);
      return;
    }
    // by default go back to user dahsboard
    history.push(ROUTE_USER_DASHBOARD);
  }, [location, history]);

  // callback to get more groups from pagination
  const fetchMorePromise = useRef<Promise<any> | null>(null);
  const fetchMoreGroups = useCallback(() => {
    if (!workflow) return;

    const {
      rowId,
      groups: { pageInfo }
    } = workflow;

    // handleFetchMore(fetchMorePromise, fetchMore, rowId, pageInfo, searchGroups);

    if (fetchMorePromise.current) return;

    const { endCursor } = pageInfo;

    fetchMorePromise.current = fetchMore({
      query: WORKFLOW_GROUPS_QUERY,
      variables: {
        workflowRowId: rowId,
        orderBy: GroupsOrderBy.NameDesc,
        first: GROUPS_PAGINATION_SIZE,
        cursor: endCursor,
        filter: getFilterSetupFromSearchStr(searchGroups)
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousGroups = previousResult.workflowByRowId?.groups;
        const newGroups = fetchMoreResult.workflowByRowId?.groups;

        if (!previousResult.workflowByRowId) {
          return previousResult;
        }

        return {
          workflowByRowId: {
            ...previousResult.workflowByRowId,
            groups: {
              ...previousGroups,
              totalCount: previousGroups?.totalCount ?? 0,
              pageInfo: {
                ...previousGroups?.pageInfo,
                hasNextPage: previousGroups?.pageInfo?.hasNextPage ?? false,
                endCursor: previousGroups?.pageInfo?.endCursor ?? null,
                ...newGroups?.pageInfo
              },
              nodes: [
                ...(previousGroups?.nodes ?? []),
                ...(newGroups?.nodes ?? [])
              ]
            }
          }
        };
      }
    }).then(() => {
      fetchMorePromise.current = null;
    });
  }, [workflow, fetchMore, searchGroups]);

  // add group mutation callback
  const addGroup = useCallback(
    async groupName => {
      // close the modal
      switchShowAddGroupModal();

      // generate a group key as 'group-idx'
      const {
        rowId,
        name,
        nonFilteredGroups: { totalCount: nbGroups } = {}
      } = workflow || {};

      if (!(rowId && name && nbGroups)) {
        const errMsg =
          'Cannot add group: Workflow rowId or name or nbGroups is missing.';
        console.error(errMsg);
        notify.error(errMsg);
        return;
      }

      const promise = addGroupMutation({
        variables: {
          workflowRowId: rowId,
          name: groupName,
          key: `group-${nbGroups + 1}`,
          orderBy: GroupsOrderBy.NameDesc,
          first: GROUPS_PAGINATION_SIZE,
          filter: getFilterSetupFromSearchStr(searchGroups)
        }
      });

      notify.promise(promise, {
        loading: `Adding group ${groupName}...`,
        success: `Group ${groupName} was correctly added to Workflow ${name}`,
        error: `Something went wrong when adding Group ${groupName} to Workflow ${name}`
      });

      const [err, response] = await to(promise);

      if (err) {
        return;
      }

      const newGroupRowId = response?.data?.createGroup?.group?.rowId;

      if (!newGroupRowId) {
        const errMsg = 'Cannot add group: New group rowId is missing.';
        console.error(errMsg);
        notify.error(errMsg);
        return;
      }
      history.push(Path.compile(ROUTE_GROUP_SETTINGS)({ id: newGroupRowId }));
    },
    [switchShowAddGroupModal, workflow, addGroupMutation, searchGroups, history]
  );

  return (
    <>
      <CustomHeader loading={loading} workflow={workflow} />
      <main className="fixed top-full-navbar right-[var(--workspace-frame-headroom,0px)] left-0 flex h-[calc(100vh-var(--spacing-full-navbar)-var(--workspace-frame-headroom,0px))] flex-col flex-nowrap items-stretch bg-background text-foreground">
        <SubHeader
          isLoading={loading}
          openAddGroupModal={switchShowAddGroupModal}
          searchGroups={searchGroups}
          updateSearch={updateSearch}
          workflow={workflow}
        />
        <div className="grow overflow-auto">
          <GroupsList
            fetchMoreGroups={fetchMoreGroups}
            hasFilters={!!searchGroups}
            isLoading={loading}
            workflow={workflow}
          />
        </div>
        <footer className="flex h-[63px] shrink-0 flex-row flex-nowrap items-start justify-between px-5 py-2.5">
          <Button
            aria-label="Click to go back"
            data-testid={TestId.WorkflowGroups.GoBackButton}
            onClick={onGoBackClick}
            variant="outline"
          >
            Go Back <Undo2 />
          </Button>
        </footer>
      </main>
      {showAddGroupModal && (
        <AddGroupModal
          isOpen={showAddGroupModal}
          onOpenChange={switchShowAddGroupModal}
          submit={addGroup}
          workflowName={workflow?.name ?? ''}
        />
      )}
    </>
  );
};

// build an or filter setup from a search string with comma-separated searches
const getFilterSetupFromSearchStr = (
  searchStr: string
): InputMaybe<GroupFilter> | undefined => {
  if (!searchStr) return;

  return {
    or: searchStr.split(',').map(searchPart => ({
      name: { likeInsensitive: `%${searchPart.trim()}%` }
    }))
  };
};

type HeaderProps = {
  workflow: WorkflowGroupsQueryQuery['workflowByRowId'];
  loading: boolean;
};
const CustomHeader = memo(({ workflow, loading }: HeaderProps) => {
  const headerConfig = {
    loading: loading || !workflow,
    bottomLevel: {
      workflowPage: true,
      infoContext: {
        links: [
          ...(workflow
            ? [
                {
                  icon: 'TableColumns',
                  label: workflow.name ?? 'Workflow',
                  path: Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
                    id: workflow.rowId
                  })
                }
              ]
            : []),
          {
            icon: 'Group',
            label: 'Groups settings'
          }
        ]
      }
    }
  } satisfies NavbarConfig;

  return <Navbar config={headerConfig} />;
});
